import {AttrsSchema, Component, ConfigOptions} from 'panel';
import copy from 'clipboard-copy';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ButtonTheme} from 'common/components/button/constants';
import {emitTrackEvent} from 'common/util/dom';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {publicDashboardLinksClient} from 'common/report/queries/app-api/public-dashboard-links-client';
import {getWorkspaceId} from 'common/report/util';
import {SharableEntityType} from 'common/report/util/saved-entities/types';
import {reporter} from 'common/report/error-reporting';
import {currentProjectStore} from 'common/report/stores/project-details';
import {useStore} from 'common/util/component-helpers';
import {CleanerURLsEnabledTrackingProp, hasCleanerUrlsEnabled} from 'common/report/util/bookmark-urls';

import {
  commonSharingTrackProps,
  CopyLinkUISource,
  eventPrefix,
  ShareButtonUISource,
  SharingTrackArgs,
  track,
} from '../tracking';
import {sharingUtils} from '../utils';
import {SharingStatusButtonAttrs, SharingStatusButtonState} from './types';
import {template} from './template';
import css from './index.inline.styl';

interface StatusButtonJsxProps {
  attrs?: SharingStatusButtonAttrs;
}

export enum CustomEvents {
  GrantFetchFailed = `failedGrantFetch`,
  GrantFetchSuccess = `successGrantFetch`,
}

export class SharingStatusButton extends Component<SharingStatusButtonState, SharingStatusButtonAttrs> {
  static get attrsSchema(): AttrsSchema<SharingStatusButtonAttrs> {
    return {
      ...super.attrsSchema,
      disabled: `boolean`,
      theme: {type: `string`, enum: Object.values(ButtonTheme), default: ButtonTheme.Secondary},
      type: `string`,
      id: `number`,
      'show-shortlink-text': `boolean`,
      'hide-share-button': {
        type: `boolean`,
        description: `Whether to include 'Share' button along with shortlink button`,
      },
      'tooltip-text': `string`,
      'project-id': `number`,
    };
  }

  private extraTracking: {[name: string]: Obj};

  get config(): ConfigOptions<SharingStatusButtonState> {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        error: null,
        data: null,
        currentUrl: ``,
        currentShortUrl: ``,
      },
      helpers: {
        isUnsavedReport: () => !this.attr(`id`),
        projectStoreFetched: () => currentProjectStore.hasFetchedValue(),
        isDisabled: () =>
          this.attr(`disabled`) ||
          !sharingUtils.validateSharingAttributes({
            projectId: this.attr(`project-id`),
            id: this.attr(`id`),
            type: this.attr(`type`),
            options: {ignoreEntityId: true},
          }),
        getPublicDashboardLink: async () => {
          try {
            // fetch from project API as workspace api returns 404 if no link exists
            const response = await publicDashboardLinksClient.getPublicDashboardLinksByDashboardId({
              dashboardId: this.attr(`id`).toString(),
              projectId: this.attr(`project-id`).toString(),
            });
            const currentPublicDashboardLink = response.find(
              (publicDashboardLink) =>
                publicDashboardLink.dashboard_id === this.attr(`id`) &&
                publicDashboardLink.workspace_id === parseInt(getWorkspaceId()) &&
                publicDashboardLink.deleted === null,
            );
            return currentPublicDashboardLink;
          } catch (err) {
            reporter.error(`Could not fetch public dashboards data in share button`);
            throw err;
          }
        },
        copyToClipboard: async (url: string) => {
          try {
            await copy(url);
            addToast({message: `URL copied!`, type: ToastType.SUCCESS});
            this.track(track.shortLink(url, CopyLinkUISource.SplitButton));
          } catch (err) {
            // If copy fails, use toast to show the URL
            // This is likely to happen in non chrome browsers when copy clipboard
            // is not a direct result of a user action
            addToast({
              message: url,
              type: ToastType.INFO,
              cta: `Copy URL`,
              ctaCallback: async () => {
                try {
                  await copy(url);
                  addToast({message: `URL copied!`, type: ToastType.SUCCESS});
                  this.track(track.shortLink(url, CopyLinkUISource.SplitButton));
                } catch (err) {
                  addToast({message: `Something went wrong. Could not copy URL`, type: ToastType.ERROR});
                  this.track(track.shortLinkCopyFailed(CopyLinkUISource.SplitButton));
                }
              },
            });
          }
        },
        copyLink: async () => {
          let shortUrl = null;
          const newUrl = window.location.toString();

          // when a dashboard has a public link in a user's current dataview
          // we fetch the latest public dashboard data, and copy the `/p` link
          if (
            this.attr(`type`) === SharableEntityType.Dashboard &&
            currentProjectStore.value.hasPublicDashboardsEnabled
          ) {
            try {
              const publicDashboardLink = await this.helpers.getPublicDashboardLink();
              if (publicDashboardLink) {
                const publicUrl = sharingUtils.createPublicDashboardLink(publicDashboardLink.public_link_id);
                await this.helpers.copyToClipboard(publicUrl);
                return;
              }
            } catch (err) {
              addToast({message: `Something went wrong. Could not copy URL`, type: ToastType.ERROR});
              return;
            }
          }

          // If url hasn't changed, return the current short url
          if (newUrl === this.state.currentUrl) {
            shortUrl = this.state.currentShortUrl;
          } else {
            try {
              shortUrl = await sharingUtils.createShortUrl(window.location.toString());
            } catch (err) {
              addToast({message: `Something went wrong. Could not copy URL`, type: ToastType.ERROR});
              this.track(track.shortLinkCopyFailed(CopyLinkUISource.SplitButton));
              return;
            }
          }

          await this.helpers.copyToClipboard(shortUrl);
          this.update({currentUrl: newUrl, currentShortUrl: shortUrl});
        },
        handleClick: () => {
          // When a user clicks on share button in brand new report, avoid tracking until new report is saved w/ id
          if (!this.helpers.isUnsavedReport()) {
            this.track(track.shareButtonClick(ShareButtonUISource.SplitShareButton));
          }
          this.dispatchEvent(new CustomEvent(`share`, {}));
        },
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    useStore(this, currentProjectStore).fetch();
  }

  set extraTrackingParams(val: {[name: string]: Obj}) {
    this.extraTracking = val;
  }

  get extraTrackingParams(): {[name: string]: Obj} {
    return this.extraTracking;
  }

  track({eventName, props}: SharingTrackArgs) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix,
      props: {
        [CleanerURLsEnabledTrackingProp]: hasCleanerUrlsEnabled(currentProjectStore.value),
        ...commonSharingTrackProps(),
        ...props,
        ...this.extraTrackingParams,
      },
    });
  }
}

export const SharingStatusButtonEl = defineAndMakeJsxFactory<StatusButtonJsxProps>(
  `mp-sharing-status-button`,
  SharingStatusButton,
);
export const SharingStatusButtonReact = panel2react<StatusButtonJsxProps>(
  `mp-sharing-status-button`,
  SharingStatusButton,
);
